import { useState, useRef, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../config/supabase';
import { useCollection } from '../../../hooks/useCollection';
import { logAudit, emptyRequest, fullName } from '../utils/blacklistService';
import BlacklistSubmitForm from './BlacklistSubmitForm';

const riskColor = (l) => ({ critical: '#7f1d1d', high: '#dc2626', medium: '#f59e0b', low: '#64748b' }[String(l || '').toLowerCase()] || '#64748b');

// Anti-enumeration: min delay between searches + per-minute cap (client-side).
const MIN_INTERVAL_MS = 1500;
const MAX_PER_MIN = 20;

function cleanStr(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function matchesRecord(rec, query) {
  const qClean = cleanStr(query);
  if (!qClean || qClean.length < 2) return false;

  const d = rec.data || rec;
  const cnieClean = cleanStr(d.cnie);
  const licenseClean = cleanStr(d.license);
  const passportClean = cleanStr(d.passport);

  // Exact or substring match on IDs (CNIE / License / Passport)
  if (cnieClean && (cnieClean === qClean || (qClean.length >= 3 && (cnieClean.includes(qClean) || qClean.includes(cnieClean))))) return true;
  if (licenseClean && (licenseClean === qClean || (qClean.length >= 3 && (licenseClean.includes(qClean) || qClean.includes(licenseClean))))) return true;
  if (passportClean && (passportClean === qClean || (qClean.length >= 3 && (passportClean.includes(qClean) || qClean.includes(passportClean))))) return true;

  // Name matching
  const fn = String(d.firstName || '').toLowerCase().trim();
  const ln = String(d.lastName || '').toLowerCase().trim();
  const full1 = `${fn} ${ln}`.trim();
  const full2 = `${ln} ${fn}`.trim();
  const fullClean1 = cleanStr(full1);
  const fullClean2 = cleanStr(full2);

  const qLower = String(query).toLowerCase().trim();

  if (fullClean1 && fullClean1 === qClean) return true;
  if (fullClean2 && fullClean2 === qClean) return true;

  if (full1 && (full1.includes(qLower) || qLower.includes(full1))) return true;
  if (full2 && (full2.includes(qLower) || qLower.includes(full2))) return true;

  // Substring match on first name or last name individually if query >= 3 chars
  if (qLower.length >= 3) {
    if (fn && (fn.includes(qLower) || qLower.includes(fn))) return true;
    if (ln && (ln.includes(qLower) || qLower.includes(ln))) return true;
  }

  return false;
}

export default function BlacklistCheckPage({ settings = {} }) {
  const { lang } = useLanguage();
  const { isDark } = useTheme();
  const { agencyId, user } = useAuth();
  const isAr = lang === 'ar';
  const t = (fr, ar) => (isAr ? ar : fr);

  // Agency's own blacklist submission requests (agency-scoped).
  const { data: myRequests, add: addRequest, update: updateRequest } = useCollection('blacklist_requests');
  const [submitForm, setSubmitForm] = useState(null); // record being submitted/edited
  const [submitting, setSubmitting] = useState(false);

  const [value, setValue] = useState('');
  const [result, setResult] = useState(null); // null | { clean:true } | { clean:false, records:[] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const lastRef = useRef(0);
  const countRef = useRef({ min: 0, at: Date.now() });

  const cardBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const txt = isDark ? 'text-white' : 'text-gray-900';

  const search = async () => {
    const v = value.trim();
    setError('');
    if (v.length < 2) { setError(t('Saisissez au moins 2 caractères.', 'أدخل حرفين على الأقل.')); return; }

    // Throttle to prevent enumeration.
    const now = Date.now();
    if (now - lastRef.current < MIN_INTERVAL_MS) { setError(t('Veuillez patienter avant une nouvelle recherche.', 'يرجى الانتظار قبل بحث جديد.')); return; }
    if (now - countRef.current.at > 60000) countRef.current = { min: 0, at: now };
    if (countRef.current.min >= MAX_PER_MIN) { setError(t('Trop de recherches. Réessayez dans une minute.', 'عمليات بحث كثيرة. أعد المحاولة بعد دقيقة.')); return; }
    lastRef.current = now; countRef.current.min++;

    setLoading(true); setResult(null);
    try {
      const safeRpc = async (p_type) => {
        try {
          const res = await supabase.rpc('blacklist_search', { p_type, p_value: v });
          return res;
        } catch (_) {
          return { data: [] };
        }
      };

      const safeTableFetch = async () => {
        try {
          const res = await supabase.from('blacklist').select('*');
          return res;
        } catch (_) {
          return { data: [] };
        }
      };

      const [resAuto, resCnie, resLic, resName, resTable] = await Promise.all([
        safeRpc('auto'),
        safeRpc('cnie'),
        safeRpc('license'),
        safeRpc('name'),
        safeTableFetch()
      ]);

      const mapById = new Map();

      // Collect RPC results
      [resAuto, resCnie, resLic, resName].forEach(res => {
        if (Array.isArray(res?.data)) {
          res.data.forEach(item => {
            if (item && item.id) mapById.set(item.id, item);
          });
        }
      });

      // Filter direct table results with robust matcher
      if (Array.isArray(resTable?.data)) {
        resTable.data.forEach(r => {
          if (r && r.data && matchesRecord(r, v)) {
            const doc = {
              id:           r.id,
              firstName:    r.data.firstName,
              lastName:     r.data.lastName,
              fullName:     `${r.data.firstName || ''} ${r.data.lastName || ''}`.trim(),
              cnie:         r.data.cnie,
              license:      r.data.license,
              city:         r.data.city,
              address:      r.data.address,
              reason:       r.data.reason,
              incident:     r.data.incident,
              riskLevel:    r.data.riskLevel,
              notes:        r.data.notes,
              sourceAgency: r.data.sourceAgency,
              outstanding:  r.data.outstanding,
              currency:     r.data.currency,
              addedAt:      r.data.addedAt || r.created_at,
              attachments:  r.data.attachments
            };
            if (String(r.data.status || 'active').toLowerCase() !== 'inactive') {
              mapById.set(r.id, doc);
            }
          }
        });
      }

      const records = Array.from(mapById.values());
      const clean = records.length === 0;
      setResult(clean ? { clean: true } : { clean: false, records });

      // Audit every search
      logAudit('viewed', {
        actor: user?.email || user?.id || agencyId, agencyId,
        targetName: v,
        details: { context: 'blacklist_check', searchType: 'auto', searchValue: v, result: clean ? 'CLEAN' : 'BLACKLISTED' },
      });
    } catch (e) {
      setError('❌ ' + (e.message || 'Erreur'));
    }
    setLoading(false);
  };

  // ── Submission (agency → pending request) ──
  const reqStats = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, more_info: 0 };
    myRequests.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [myRequests]);

  const submitRequest = async () => {
    const f = submitForm;
    if (!f.firstName?.trim() || !f.lastName?.trim()) { setError(t('Prénom et Nom requis.', 'الاسم واللقب مطلوبان.')); return; }
    if (!f.cnie && !f.license && !f.passport && !f.phone) { setError(t('Au moins un identifiant requis (CNIE/Permis/Passeport/Tél).', 'مطلوب معرّف واحد على الأقل.')); return; }
    setSubmitting(true);
    try {
      // Duplicate detection against the GLOBAL blacklist (secure RPC).
      if (!f.id) {
        for (const [ptype, val] of [['cnie', f.cnie], ['license', f.license]]) {
          if (val && val.trim().length >= 3) {
            const { data } = await supabase.rpc('blacklist_search', { p_type: ptype, p_value: val.trim() });
            if (Array.isArray(data) && data.length) {
              if (!confirm(t(`⚠️ Une personne avec ce ${ptype === 'cnie' ? 'C.N.I.E' : 'permis'} existe déjà dans la liste noire.\nEnvoyer quand même ?`,
                             '⚠️ يوجد شخص بنفس المعرّف في القائمة. إرسال رغم ذلك؟'))) { setSubmitting(false); return; }
            }
          }
        }
      }
      const payload = {
        ...f,
        fullName: fullName(f),
        status: 'pending',
        rejectReason: '', moreInfoMessage: '',
        agencyName: settings.companyName || settings.agencyName || agencyId,
        agencyChatId: settings.telegramChatId || '',
        submittedBy: user?.email || user?.id || agencyId,
        submittedAt: f.submittedAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      if (f.id) {
        await updateRequest(f.id, payload);
        await logAudit('edited', { actor: user?.email || agencyId, agencyId, targetName: fullName(f), details: { context: 'request_resubmit' } });
      } else {
        const ins = await addRequest(payload);
        await logAudit('submitted', { actor: user?.email || agencyId, agencyId, targetId: ins?.id, targetName: fullName(f), details: { context: 'request_submitted' } });
      }
      setSubmitForm(null); setError('');
    } catch (e) { setError('❌ ' + e.message); }
    setSubmitting(false);
  };

  const reqBadge = (st) => {
    const map = {
      pending: ['bg-amber-100 text-amber-700', t('En attente', 'قيد الانتظار')],
      approved: ['bg-green-100 text-green-700', t('Approuvée', 'مقبولة')],
      rejected: ['bg-red-100 text-red-700', t('Rejetée', 'مرفوضة')],
      more_info: ['bg-blue-100 text-blue-700', t('Infos requises', 'مطلوب معلومات')],
    };
    const [cls, label] = map[st] || map.pending;
    return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto" dir={isAr ? 'rtl' : 'ltr'}>
      {submitForm && (
        <BlacklistSubmitForm value={submitForm} onChange={setSubmitForm} onSubmit={submitRequest} onCancel={() => { setSubmitForm(null); setError(''); }} busy={submitting} />
      )}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <h1 className={`text-2xl font-black flex items-center gap-2 ${txt}`}>🔎 {t('Vérification Liste Noire', 'التحقق من القائمة السوداء')}</h1>
        <button onClick={() => { setSubmitForm(emptyRequest()); setError(''); }}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm">
          🚩 {t('Signaler un client', 'الإبلاغ عن عميل')}
        </button>
      </div>
      <p className={`text-sm -mt-3 mb-4 ${sub}`}>
        {t('Vérifiez un client AVANT de créer un contrat. Seul le résultat exact est affiché.',
           'تحقّق من العميل قبل إنشاء العقد. يُعرض فقط النتيجة المطابقة تماماً.')}
      </p>

      {/* Search box */}
      <div className={`rounded-2xl border p-4 ${cardBg}`}>
        <div className="flex gap-2 flex-wrap">
          <input
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder={t('Nom complet, C.N.I.E (Ex: AB123456) ou N° permis...', 'الاسم الكامل، ب.ت.و (مثال: AB123456) أو رقم الرخصة...')}
            className={`flex-1 min-w-[200px] px-4 py-3 rounded-xl border text-sm ${isDark ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'}`}
          />
          <button onClick={search} disabled={loading}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm disabled:opacity-60 cursor-pointer">
            {loading ? '⏳' : '🔎 ' + t('Vérifier', 'تحقّق')}
          </button>
        </div>
        {error && <div className="mt-2 text-xs font-bold text-red-600">{error}</div>}
      </div>

      {/* Result */}
      {result && (
        <div className="mt-5">
          {result.clean ? (
            <div className="rounded-2xl border-2 border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-6 text-center">
              <div className="text-5xl mb-2">🟢</div>
              <div className="text-2xl font-black text-green-700 dark:text-green-300">{t('CLIENT CLEAN', 'العميل سليم')}</div>
              <div className={`text-sm mt-1 ${sub}`}>{t('Aucun enregistrement trouvé dans la liste noire.', 'لا يوجد سجلّ في القائمة السوداء.')}</div>
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-red-400 dark:border-red-800 overflow-hidden">
              <div className="bg-red-600 text-white p-4 text-center">
                <div className="text-5xl mb-1">🔴</div>
                <div className="text-2xl font-black">{t('CLIENT SUR LISTE NOIRE', 'العميل في القائمة السوداء')}</div>
              </div>
              <div className={`p-4 ${cardBg}`}>
                {result.records.map((r, i) => (
                  <div key={i} className={`${i > 0 ? 'mt-4 pt-4 border-t border-gray-200 dark:border-gray-700' : ''}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className={`text-lg font-black ${txt}`}>👤 {r.fullName || '—'}</div>
                      <span style={{ background: riskColor(r.riskLevel) }} className="text-white px-3 py-1 rounded-full text-xs font-bold">
                        {t('Risque', 'الخطورة')}: {r.riskLevel || 'Medium'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <Info label={t('C.N.I.E', 'ب.ت.و')} value={r.cnie} txt={txt} sub={sub} />
                      <Info label={t('Permis', 'الرخصة')} value={r.license} txt={txt} sub={sub} />
                      <Info label={t('Ville', 'المدينة')} value={r.city} txt={txt} sub={sub} />
                      <Info label={t('Ajouté le', 'أُضيف في')} value={r.addedAt ? new Date(r.addedAt).toLocaleDateString(isAr ? 'ar-MA' : 'fr-FR') : ''} txt={txt} sub={sub} />
                      <Info label={t('Agence déclarante', 'الوكالة المُبلِّغة')} value={r.sourceAgency} txt={txt} sub={sub} />
                      <Info label={t('Montant dû', 'المبلغ المستحق')} value={r.outstanding ? `${r.outstanding} ${r.currency || 'DH'}` : ''} txt={txt} sub={sub} />
                      <div className="sm:col-span-2"><Info label={t('Adresse', 'العنوان')} value={r.address} txt={txt} sub={sub} /></div>
                      <div className="sm:col-span-2"><Info label={t('Motif', 'السبب')} value={r.reason} txt={txt} sub={sub} /></div>
                      {r.incident && <div className="sm:col-span-2"><Info label={t('Incident', 'الحادثة')} value={r.incident} txt={txt} sub={sub} /></div>}
                      {r.notes && <div className="sm:col-span-2"><Info label={t('Notes', 'ملاحظات')} value={r.notes} txt={txt} sub={sub} /></div>}
                    </div>
                    {Array.isArray(r.attachments) && r.attachments.length > 0 && (
                      <div className="mt-2 text-sm">
                        <span className={`font-bold ${txt}`}>📎 {t('Documents', 'مستندات')}:</span>{' '}
                        {r.attachments.map((a, j) => (
                          <a key={j} href={a.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline mr-2">{a.name || `Doc ${j + 1}`}</a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="mt-3 text-xs font-bold text-red-600">
                  ⚠️ {t('Il est recommandé de NE PAS créer de contrat avec ce client.', 'يُنصح بعدم إنشاء عقد مع هذا العميل.')}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* My submission requests — widgets + list */}
      <div className="mt-8">
        <h2 className={`font-black text-lg mb-3 ${txt}`}>📤 {t('Mes signalements', 'إبلاغاتي')}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {[
            ['⏳', t('En attente', 'قيد الانتظار'), reqStats.pending, '#f59e0b'],
            ['✅', t('Approuvés', 'مقبولة'), reqStats.approved, '#10b981'],
            ['❌', t('Rejetés', 'مرفوضة'), reqStats.rejected, '#ef4444'],
            ['ℹ️', t('Infos requises', 'معلومات مطلوبة'), reqStats.more_info, '#3b82f6'],
          ].map(([icon, label, val, color]) => (
            <div key={label} className={`rounded-2xl border p-4 ${cardBg}`}>
              <div className="flex items-center justify-between"><span className="text-2xl">{icon}</span><span className="text-2xl font-black" style={{ color }}>{val}</span></div>
              <div className={`text-[11px] font-bold mt-1 ${sub}`}>{label}</div>
            </div>
          ))}
        </div>
        {myRequests.length === 0 ? (
          <div className={`text-center py-6 text-sm ${sub} rounded-2xl border ${cardBg}`}>{t('Aucun signalement envoyé.', 'لا توجد إبلاغات مُرسلة.')}</div>
        ) : (
          <div className="space-y-2">
            {[...myRequests].sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0)).map(r => (
              <div key={r.id} className={`rounded-xl border p-3 ${cardBg} flex items-start justify-between gap-3 flex-wrap`}>
                <div className="min-w-0">
                  <div className={`font-bold ${txt}`}>👤 {r.fullName || fullName(r)} {reqBadge(r.status)}</div>
                  <div className={`text-xs ${sub}`}>{r.reason || '—'} · {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString(isAr ? 'ar-MA' : 'fr-FR') : ''}</div>
                  {r.status === 'rejected' && r.rejectReason && <div className="text-xs text-red-600 mt-1">❌ {t('Motif du rejet', 'سبب الرفض')}: {r.rejectReason}</div>}
                  {r.status === 'more_info' && r.moreInfoMessage && <div className="text-xs text-blue-600 mt-1">ℹ️ {r.moreInfoMessage}</div>}
                </div>
                {(r.status === 'pending' || r.status === 'more_info') && (
                  <button onClick={() => { setSubmitForm({ ...emptyRequest(), ...r }); setError(''); }}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white">✏️ {t('Modifier', 'تعديل')}</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, txt, sub }) {
  return (
    <div>
      <span className={sub}>{label}: </span>
      <span className={`font-bold ${txt}`}>{value || '—'}</span>
    </div>
  );
}
